import createClient from "openapi-fetch";
import { useSyncExternalStore } from "react";
import { config } from "../../config";
import { serviceURL } from "../../utils/service-url";

import type { paths } from "../../__definitions__/flags.changeengine.com-definitions";

const overrides =
  typeof localStorage === "undefined"
    ? {}
    : // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      (JSON.parse(localStorage.getItem("flags") || "{}") as {
        [key: string]: boolean;
      });

let features: { [key: string]: { enabled: boolean; variables: unknown } } = {};
let featuresLoading = true;
let featuresSubscriptions: (() => void)[] = [];

const FLAGS_TIMEOUT_MS = 10_000;

export const setOptimizelyUser = async (token: string): Promise<void> => {
  if (!featuresLoading) return;
  const client = createClient<paths>({
    baseUrl: serviceURL("flags"),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  try {
    const response = await client.GET(`/features/{sdkKey}`, {
      params: { path: { sdkKey: config.optimizely.sdkKey } },
      signal: AbortSignal.timeout(FLAGS_TIMEOUT_MS),
    });
    if (response.error) throw new Error("Error fetching flags");
    features = response.data;
  } catch (error) {
    console.error("Failed to load feature flags, continuing with defaults", error);
    features = {};
  } finally {
    featuresLoading = false;
    featuresSubscriptions.forEach((l) => l());
  }
};

const getFlag = (flag: string): [boolean, unknown, boolean, boolean] =>
  flag in overrides
    ? [overrides[flag]!, {}, true, false]
    : featuresLoading
      ? [false, {}, false, false]
      : flag in features
        ? [features[flag]!.enabled, features[flag]!.variables, true, false]
        : [false, {}, true, false];

export const useFlag = (flag: string): [boolean, unknown, boolean, boolean] => {
  useSyncExternalStore(
    (listener) => {
      featuresSubscriptions = [...featuresSubscriptions, listener];
      return () => {
        featuresSubscriptions = featuresSubscriptions.filter((l) => l !== listener);
      };
    },
    () => features,
  );
  return getFlag(flag);
};

/**
 * Rejects a list whose length is not known at compile time, so `...someArray`
 * is a type error while `useFlags("a", "b")` and `useFlags(...AS_CONST)` are not.
 */
type FixedList<T extends readonly unknown[]> = number extends T["length"]
  ? ["useFlags needs a fixed list of flag literals"]
  : T;

/**
 * Reads several flags at once.
 *
 * Calls `useFlag` once per flag, so the list must be fixed at each call site or
 * the hook count would vary between renders. `FixedList` enforces that, which is
 * why the rules-of-hooks warning below is safe to suppress.
 */
export function useFlags<const T extends readonly string[]>(
  ...flags: FixedList<T>
): { flags: Record<T[number], boolean>; flagsLoaded: boolean } {
  const flagsMap = [] as { flag: T[number]; enabled: boolean; loaded: boolean }[];
  for (const flag of flags as readonly T[number][]) {
    // oxlint-disable-next-line react/hooks -- FixedList keeps the list a compile-time tuple, so the hook count is stable
    const [enabled, , loaded] = useFlag(flag);
    flagsMap.push({ flag, enabled, loaded });
  }

  return {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    flags: Object.fromEntries(flagsMap.map(({ flag, enabled }) => [flag, enabled])) as Record<
      T[number],
      boolean
    >,
    flagsLoaded: flagsMap.every(({ loaded }) => loaded),
  };
}

export const getFlags = (): {
  [key: string]: {
    enabled: boolean;
    source: "Optimizely" | "Override";
    variables: unknown;
  };
} =>
  Object.fromEntries(
    Object.entries(features).map(([k, v]) => [
      k,
      { ...v, source: k in overrides ? "Override" : "Optimizely" },
    ]),
  );

const ls = (): void => {
  console.table(features);
};

class FeatureFlags {
  get ls(): void {
    ls();
    return;
  }

  getFlags(): Record<string, { enabled: boolean; source: "Optimizely" | "Override" }> {
    return getFlags();
  }

  toggle(flag: string): void {
    const [enabled] = getFlag(flag);
    overrides[flag] = !enabled;
    localStorage.setItem("flags", JSON.stringify(overrides));
    ls();
  }

  clear(): void {
    for (const flag of Object.keys(overrides)) {
      Reflect.deleteProperty(overrides, flag);
    }
    localStorage.setItem("flags", "{}");
  }

  reset(flag: string): void {
    Reflect.deleteProperty(overrides, flag);
    localStorage.setItem("flags", JSON.stringify(overrides));
  }
}

declare global {
  // Attached to globalThis in dev only, as a console debugging aid.
  var ff: FeatureFlags;
}

if (config.Dev) {
  globalThis.ff = new FeatureFlags();
  console.log(`Feature Flags:
        ff.ls: List all feature flags
        ff.toggle('flag'): Toggle a feature flag
        ff.clear(): Clear all feature flag overrides
      `);
}
